/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
// AFP-2532 TODO: Fix automatic suppressions below
// https://product-fabric.atlassian.net/browse/AFP-2532
// eslint-disable-next-line @atlassian/tangerine/import/entry-points
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { completeColors, inProgressColors } from './ChecklistBackground';
import { token } from '@atlaskit/tokens';
import { type PropsWithChildren, type MouseEventHandler } from 'react';
import { CHECKLIST_TITLE_ID } from '../../common/constants';

const dismissButtonWrapperStyles = css({
	flexShrink: 0,
});
const flexContentStyles = css({
	position: 'relative',
	boxSizing: 'border-box',
	display: 'flex',
	flexDirection: 'column',
});
const footerTitleStyles = xcss({
	paddingBottom: 'space.100',
});
const FADE_DURATION = '0.5s';
export const CHECKLIST_BACKGROUND_HEIGHT = 140;
const isSafari = () => /^((?!chrome|android).)*safari/i.test((navigator as Navigator).userAgent);

export const OverflowWrapper = ({ children, ...restProp }: PropsWithChildren<{}>) => (
	<div
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css={css({
			overflowY: 'auto',
			overflowX: 'hidden',
			background: token('color.background.neutral', '#f4f5f7'),
			width: '100%',
			height: '100%',
			position: 'relative',
		})}
		role="dialog"
		aria-labelledby={CHECKLIST_TITLE_ID}
		{...restProp}
	>
		{children}
	</div>
);

export const Background = ({ done, children }: PropsWithChildren<{ done: boolean }>) => (
	<div
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css={css({
			position: 'absolute',
			top: 0,
			left: 0,
			right: 0,
			width: 'auto',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'#Rectangle': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				fill: done ? completeColors.mainFillColor : inProgressColors.mainFillColor,
				transition: `all ${FADE_DURATION} ease-in-out`,
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'#linearGradient': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
				'stop:first-of-type': {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					stopColor: done ? completeColors.gradientFillColor : inProgressColors.gradientFillColor,
					transition: `all ${FADE_DURATION} ease-in-out`,
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
				'stop:last-of-type': {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					stopColor: done ? completeColors.gradientFillColor : inProgressColors.gradientFillColor,
					transition: `all ${FADE_DURATION} ease-in-out`,
				},
			},
		})}
	>
		{children}
	</div>
);

export const FlexContent = ({ children }: PropsWithChildren<{}>) => (
	<div css={flexContentStyles}>{children}</div>
);

export const ContentWrapper = ({ children }: PropsWithChildren<{}>) => (
	<div
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css={css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			zIndex: isSafari() ? 'initial' : 1,
			position: 'relative',
		})}
	>
		{children}
	</div>
);

export const HeaderWrapper = ({ children }: PropsWithChildren<{}>) => (
	<div
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css={css({
			display: 'flex',
			alignItems: 'flex-start',
			justifyContent: 'space-between',
			padding: token('space.100', '8px'),
		})}
	>
		{children}
	</div>
);

export const BodyWrapper = ({ children }: PropsWithChildren<{}>) => (
	<div
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css={css({
			padding: `${token('space.0', '0px')} ${token('space.250', '20px')} ${token(
				'space.600',
				'48px',
			)}`,
		})}
	>
		{children}
	</div>
);

export const Footer = ({ children }: PropsWithChildren<{}>) => (
	<div
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css={css({
			background: token('color.background.neutral.subtle', '#f4f5f7'),
			paddingTop: token('space.200', '16px'),
			paddingBottom: token('space.200', '16px'),
			position: 'absolute',
			bottom: token('space.0', '0px'),
			left: token('space.0', '0px'),
			right: token('space.0', '0px'),
			textAlign: 'center',
			zIndex: 1,
		})}
	>
		{children}
	</div>
);

export const FooterTitle = ({ children }: PropsWithChildren<{}>) => (
	<Box as="p" xcss={footerTitleStyles}>
		{children}
	</Box>
);

export const FooterDismissLink = ({
	children,
	href,
	onClick,
	...rest
}: PropsWithChildren<{
	href: string;
	onClick: MouseEventHandler;
}>) => (
	// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
	<a
		href={href}
		onClick={onClick}
		{...rest}
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css={css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			color: token('color.text.discovery', colors.P300),
			font: token('font.body'),
			'&:hover': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				color: token('color.text.discovery', colors.P300),
				textDecoration: 'none',
			},
		})}
	>
		{children}
	</a>
);

export const DismissButtonWrapper = ({ children }: PropsWithChildren<{}>) => (
	<div css={dismissButtonWrapperStyles}>{children}</div>
);
